import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { User, AuthState } from '../../models/User';
import { signInWithGoogle, logOut } from '../../utils/authUtils';
import { db } from '../../services/firebaseConfig';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const ALLOWED_DOMAIN = ['gaialogic.ai', 'ddductr.de'];

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

const validateUserDomain = (email: string): boolean => {
  const isValid = ALLOWED_DOMAIN.some(domain => email.endsWith(domain));
  console.log(`🔒 Domain validation for ${email}: ${isValid ? 'ALLOWED' : 'DENIED'}`);
  return isValid;
};

/**
 * Determines organization based on email domain
 * @param email User's email address
 * @returns Organization name or empty string
 */
const getOrganizationFromEmail = (email: string): string => {
  if (email.endsWith('gaialogic.ai')) return 'GaiaLogic';
  if (email.endsWith('ddductr.de')) return 'DDDUCTR';
  return '';
};

/**
 * Creates or updates a user document in Firestore
 * @param userId The user's ID
 * @param email The user's email
 * @returns Promise that resolves when the operation is complete
 */
const createOrUpdateUserDocument = async (userId: string, email: string): Promise<void> => {
  try {
    console.log('🔍 Checking if user document exists for:', userId);
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      console.log('📝 Creating new user document for:', email);
      const timestamp = serverTimestamp();
      const organization = getOrganizationFromEmail(email);
      
      // Create new user document
      await setDoc(userDocRef, {
        uid: userId,
        email: email,
        created_at: timestamp,
        subscription_start: timestamp,
        subscription_end: null,
        organization: organization,
        user_type: 'admin', // Set as admin as requested
      });
      
      console.log('✅ User document created successfully');
    } else {
      console.log('ℹ️ User document already exists, skipping creation');
    }
  } catch (error) {
    console.error('❌ Error creating/updating user document:', error);
    throw error;
  }
};

/**
 * Fetches the user type from Firestore
 * @param userId The user's ID
 * @returns The user type ('standard' or 'admin')
 */
const fetchUserType = async (userId: string): Promise<'standard' | 'admin'> => {
  try {
    console.log('📊 Fetching user type for:', userId);
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists() && userDoc.data().user_type) {
      const userType = userDoc.data().user_type as 'standard' | 'admin';
      console.log('👤 User type found:', userType);
      return userType;
    }
    
    console.log('⚠️ No user type found, defaulting to standard');
    return 'standard'; // Default type if not specified
  } catch (error) {
    console.error('❌ Error fetching user type:', error);
    return 'standard'; // Default to standard on error
  }
};

// Add a helper function to check if token needs refreshing
// Firebase tokens typically last for 1 hour, we'll refresh if < 5 minutes remaining
const needsTokenRefresh = (user: any): boolean => {
  if (!user?.tokenExpiration) return true;
  
  // Get current time and expiration time
  const now = new Date().getTime();
  const expiration = user.tokenExpiration;
  
  // If token expires in less than 5 minutes (300000 ms), refresh it
  const refreshThreshold = 5 * 60 * 1000; // 5 minutes in milliseconds
  return now + refreshThreshold >= expiration;
};

// Add a helper to decode and extract expiration from Firebase JWT
const getTokenExpiration = (token: string): number => {
  try {
    // Firebase JWT format: header.payload.signature
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    const decodedToken = JSON.parse(jsonPayload);
    // exp contains the expiration timestamp in seconds since epoch
    return decodedToken.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error decoding token:', error);
    // Return current time to force refresh if we can't decode the token
    return new Date().getTime();
  }
};

export const refreshToken = createAsyncThunk<any, boolean | undefined>(
  'user/refreshToken',
  async (forceRefresh = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const currentUser = state.user.user;
      
      // Skip refresh if token is still valid and no force refresh requested
      if (!forceRefresh && currentUser && !needsTokenRefresh(currentUser)) {
        console.log('🔍 Token still valid, skipping refresh');
        return currentUser;
      }
      
      console.log('🔄 Starting token refresh...');
      const auth = getAuth();
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }

      // Force token refresh
      const newToken = await firebaseUser.getIdToken(true);
      
      // Get expiration time from token
      const tokenExpiration = getTokenExpiration(newToken);
      
      // Fetch user type from Firestore
      const userType = await fetchUserType(firebaseUser.uid);

      const userData = {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        username: firebaseUser.displayName || 'User',
        avatar: firebaseUser.photoURL || '',
        accessToken: newToken,
        userType: userType,
        tokenExpiration: tokenExpiration,
        lastRefreshed: new Date().getTime(),
        // Add Firebase project ID to track environment changes
        customData: {
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID
        }
      };

      console.log('✨ Token refreshed successfully, expires at:', new Date(tokenExpiration).toLocaleTimeString());
      return userData;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to refresh token');
    }
  }
);

// Add a utility thunk for smart token refresh that other thunks can use
export const getValidToken = createAsyncThunk<string, void>(
  'user/getValidToken',
  async (_, { getState, dispatch }) => {
    const state = getState() as any;
    const currentUser = state.user.user;
    
    // If no user or token needs refresh, refresh it
    if (!currentUser || needsTokenRefresh(currentUser)) {
      const refreshedUser = await dispatch(refreshToken(true)).unwrap();
      return refreshedUser.accessToken;
    }
    
    // Otherwise return existing token
    return currentUser.accessToken;
  }
);

// Async thunk for Google sign in
export const loginWithGoogle = createAsyncThunk(
  'user/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔑 Starting Google sign-in process...');
      const userData = await signInWithGoogle();

      if (!userData) {
        console.error('❌ No user data received from Google');
        throw new Error('No user data received');
      }

      console.log('👤 Received user data:', {
        email: userData.email,
        timestamp: new Date().toISOString(),
      });

      // Check if user's email domain is allowed
      if (!validateUserDomain(userData.email)) {
        console.warn(`🚫 Access denied for unauthorized domain: ${userData.email}`);
        throw new Error(`Access denied. Only @${ALLOWED_DOMAIN.join(', @')} email addresses are allowed.`);
      }
      
      // Create or update user document in Firestore
      await createOrUpdateUserDocument(userData.id, userData.email);
      
      // Fetch user type from Firestore
      const userType = await fetchUserType(userData.id);
      
      // Add userType to userData
      const enhancedUserData = {
        ...userData,
        userType
      };

      console.log('✅ Login successful for:', userData.email);
      return enhancedUserData;
    } catch (error) {
      console.error('❌ Login error:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to sign in');
    }
  }
);

// Email/password sign-in thunk, removing localStorage usage
export const loginWithEmailPassword = createAsyncThunk(
  'user/loginWithEmailPassword',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      console.log('🔑 Starting Email/Password sign-in process...');
      
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const currentUser = userCredential.user;

      // Create or update user document in Firestore
      await createOrUpdateUserDocument(currentUser.uid, currentUser.email || '');

      // Fetch a fresh token
      const token = await currentUser.getIdToken(true);
      
      // Fetch user type from Firestore
      const userType = await fetchUserType(currentUser.uid);

      const userData = {
        id: currentUser.uid,
        email: currentUser.email || '',
        username: currentUser.displayName || 'User',
        avatar: currentUser.photoURL || '',
        accessToken: token,
        userType: userType,
      };

      console.log('✅ Email/Password login successful:', userData.email);
      return userData;
    } catch (error) {
      console.error('❌ Email/Password Login error:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to sign in with Email/Password'
      );
    }
  }
);

// Logout thunk, removing localStorage usage
export const logoutUser = createAsyncThunk('user/logout', async (_, { rejectWithValue }) => {
  try {
    console.log('👋 Starting logout process...');
    await logOut();
    console.log('✅ Logout successful');
    return null;
  } catch (error) {
    console.error('❌ Logout error:', error);
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to log out');
  }
});

// Always generating a new token, removing localStorage references
export const checkAuthSession = createAsyncThunk(
  'user/checkSession',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      console.log('🔄 Checking authentication session...');
      const auth = getAuth();
      const currentUser = auth.currentUser;

      // If no user in memory, consider them not logged in
      if (!currentUser) {
        console.log('ℹ️ No existing user found');
        return null;
      }

      // Get the Firebase project ID from environment to detect environment changes
      const firebaseProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
      
      // Determine if we need to force a token refresh
      const currentUserState = (getState() as any).user.user;
      
      // Force-refresh token when:
      // 1. Token is expired or close to expiring
      // 2. Firebase project ID has changed (environment switch)
      const forceRefresh = !currentUserState || 
                          needsTokenRefresh(currentUserState) || 
                          (currentUserState?.customData?.projectId && 
                           currentUserState.customData.projectId !== firebaseProjectId);
      
      if (forceRefresh) {
        console.log('🔄 Refreshing token due to expiration or project change...');
        return dispatch(refreshToken(true)).unwrap();
      }
      
      // If token is still valid, just return the current user
      console.log('✅ Session token is valid, no refresh needed');
      return currentUserState;
    } catch (error) {
      console.error('❌ Session restoration error:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to restore session');
    }
  }
);

// Add a dedicated thunk to fetch user type
export const fetchUserTypeThunk = createAsyncThunk(
  'user/fetchUserType',
  async (userId: string, { rejectWithValue }) => {
    try {
      console.log('🔍 Fetching user type for:', userId);
      const userType = await fetchUserType(userId);
      console.log('✅ User type fetched successfully:', userType);
      return userType;
    } catch (error) {
      console.error('❌ Error fetching user type:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user type');
    }
  }
);

/**
 * Creates a new user in Firebase Auth and Firestore
 * @param userData The user data to create
 * @returns Promise with the created user data
 */
export const createNewUser = createAsyncThunk(
  'user/createNewUser',
  async (userData: {
    email: string;
    organization: string;
    user_type: 'admin' | 'standard';
    created_at?: Date | null;
    subscription_start?: Date | null;
    subscription_end?: Date | null;
    message_count?: number;
    fraktal_conversation_count?: number;
    reflection_conversation_count?: number;
    fast_thinking_conversation_count?: number;
  }, { rejectWithValue, getState }) => {
    try {
      console.log('👤 Creating new user:', userData.email);
      const auth = getAuth();
      
      // Store current user before we do anything
      const currentUser = auth.currentUser;
      const currentUserEmail = currentUser?.email;
      const currentUserCredential = null;
      
      // Store current admin user's password if we need to reauthenticate
      // This is required for proper admin operation after creating a user
      console.log('⚠️ Admin user will be restored after creating the new user');
      
      // Generate a random password (this will be replaced when user resets password)
      const tempPassword = Math.random().toString(36).slice(-10);
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, tempPassword);
      const firebaseUser = userCredential.user;
      
      // Create user document in Firestore
      const timestamp = serverTimestamp();
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      
      // Use provided dates or default to server timestamp for created_at and subscription_start
      const now = new Date();
      
      await setDoc(userDocRef, {
        uid: firebaseUser.uid,
        email: userData.email,
        created_at: userData.created_at || timestamp,
        updated_at: timestamp,
        subscription_start: userData.subscription_start || timestamp,
        subscription_end: userData.subscription_end || null,
        organization: userData.organization,
        user_type: userData.user_type,
        message_count: userData.message_count || 0,
        fraktal_conversation_count: userData.fraktal_conversation_count || 0,
        reflection_conversation_count: userData.reflection_conversation_count || 0,
        fast_thinking_conversation_count: userData.fast_thinking_conversation_count || 0,
        last_activity: now.toISOString()
      });
      
      // Send password reset email to the new user
      await sendPasswordResetEmail(auth, userData.email);
      
      // Sign out the new user and restore the admin user if needed
      if (currentUser) {
        // If we had a signed in user before, sign them back in to avoid state disruption
        // This prevents the app from remounting due to auth state changes
        await auth.signOut();
        console.log('⚠️ New user signed out, restoring admin user:', currentUserEmail);
        
        // At this point, we don't know how to sign back in as the admin without a password
        // just notify the admin that they might need to sign in again
        console.log('✅ User created successfully:', firebaseUser.uid, 'Please sign in again if needed');
      }
      
      return {
        uid: firebaseUser.uid,
        email: userData.email,
        organization: userData.organization,
        user_type: userData.user_type
      };
    } catch (error) {
      console.error('❌ Error creating user:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create user');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      console.log('📝 Manual user setup attempted for:', action.payload.email);
      // Domain validation removed from manual user setup
      
      // If userType is not provided, default to standard
      if (!action.payload.userType) {
        action.payload.userType = 'standard';
        console.log('⚠️ No user type provided, defaulting to standard');
      }
      
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
      console.log('✅ Manual user setup completed successfully');
    },
    clearUser: state => {
      console.log('🗑️ Clearing user state');
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      console.log('✅ User state cleared');
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginWithGoogle.pending, state => {
        console.log('⏳ Login process started...');
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        console.log('✨ Login successful, updating state...');
        state.user = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        console.error('❌ Login rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginWithEmailPassword.pending, state => {
        console.log('⏳ Email/Password login started...');
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithEmailPassword.fulfilled, (state, action) => {
        console.log('✨ Email/Password login successful, updating state...');
        state.user = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(loginWithEmailPassword.rejected, (state, action) => {
        console.error('❌ Email/Password login rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(logoutUser.fulfilled, state => {
        console.log('👋 Logout successful, clearing state...');
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        console.error('❌ Logout rejected:', action.payload);
        state.error = action.payload as string;
      })
      .addCase(checkAuthSession.fulfilled, (state, action) => {
        if (action.payload) {
          console.log('✅ Session check successful, user authenticated');
          state.user = action.payload;
          state.isAuthenticated = true;
        } else {
          console.log('ℹ️ Session check complete, no valid session found');
          state.user = null;
          state.isAuthenticated = false;
        }
        state.loading = false;
      })
      .addCase(checkAuthSession.rejected, (state, action) => {
        console.error('❌ Session check failed:', action.payload);
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        console.error('❌ Token refresh failed:', action.payload);
        state.error = action.payload as string;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        console.log('✅ Refresh token completed, updating user state');
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchUserTypeThunk.fulfilled, (state, action) => {
        console.log('✅ User type updated:', action.payload);
        if (state.user) {
          state.user.userType = action.payload;
        }
      })
      .addCase(fetchUserTypeThunk.rejected, (state, action) => {
        console.error('❌ Failed to fetch user type:', action.payload);
        // Don't change the user state, just log the error
      })
      .addCase(createNewUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearUser } = userSlice.actions;
export default userSlice.reducer;
